import { useState } from 'react'
import type { MessageInitShape } from '@bufbuild/protobuf'
import { useMutation } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, UserPlus } from 'lucide-react'
import { adminInviteAccount } from '@/gen/cortado/v1/admin-AdminService_connectquery'
import type { AdminInviteAccountRequestSchema } from '@/gen/cortado/v1/admin_pb'
import { PlanKeyEnum } from '@/gen/cortado/v1/subscriptions_pb'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export type InvitePlan = 'standard' | 'ccc'
export type InviteTrial = 'standard' | 'extended' | 'volume'

export interface AccountInviteForm {
  email: string
  firstName: string
  lastName: string
  companyName: string
  plan: InvitePlan
  trial: InviteTrial
  // The extended trial's length, or the volume trial's backstop.
  trialDays: string
  thresholdDollars: string
  reason: string
}

export type AccountInviteRequest = MessageInitShape<typeof AdminInviteAccountRequestSchema>

const EMPTY_FORM: AccountInviteForm = {
  email: '',
  firstName: '',
  lastName: '',
  companyName: '',
  plan: 'standard',
  trial: 'standard',
  trialDays: '',
  thresholdDollars: '',
  reason: '',
}

// What the dialog pre-fills when an admin picks a volume trial.
export const VOLUME_TRIAL_DEFAULTS = { thresholdDollars: '5000', trialDays: '90' } as const

// A Coffee Cart Collective invite is CCC pricing plus a 90-day trial.
export const CCC_PRESET: Pick<
  AccountInviteForm,
  'plan' | 'trial' | 'trialDays' | 'thresholdDollars' | 'reason'
> = {
  plan: 'ccc',
  trial: 'extended',
  trialDays: '90',
  thresholdDollars: '',
  reason: 'Invited as a Coffee Cart Collective member',
}

const PRESETS = {
  blank: { plan: 'standard', trial: 'standard', trialDays: '', thresholdDollars: '', reason: '' },
  ccc: CCC_PRESET,
} as const satisfies Record<string, Partial<AccountInviteForm>>

type PresetKey = keyof typeof PRESETS

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const WHOLE_NUMBER = /^\d+$/

// Reports whether the invite records a billing exception, which needs a reason.
export function inviteNeedsReason(form: Pick<AccountInviteForm, 'plan' | 'trial'>): boolean {
  return form.plan === 'ccc' || form.trial !== 'standard'
}

function positiveWholeNumber(value: string): number | null {
  const trimmed = value.trim()
  if (!WHOLE_NUMBER.test(trimmed) || Number(trimmed) <= 0) return null
  return Number(trimmed)
}

// Trims the form into an AdminInviteAccount request, or returns null while a
// field is missing or invalid. The backend re-validates.
export function buildAccountInviteRequest(form: AccountInviteForm): AccountInviteRequest | null {
  const email = form.email.trim()
  const firstName = form.firstName.trim()
  const lastName = form.lastName.trim()
  const companyName = form.companyName.trim()
  const reason = form.reason.trim()
  if (!firstName || !lastName || !companyName) return null
  if (!EMAIL_PATTERN.test(email)) return null
  if (inviteNeedsReason(form) && !reason) return null

  let trial: AccountInviteRequest['trial']
  if (form.trial === 'standard') {
    trial = { case: 'standardTrial', value: {} }
  } else {
    const days = positiveWholeNumber(form.trialDays)
    if (days === null) return null
    if (form.trial === 'extended') {
      trial = { case: 'extendedTrial', value: { days } }
    } else {
      const dollars = positiveWholeNumber(form.thresholdDollars)
      if (dollars === null) return null
      trial = {
        case: 'volumeTrial',
        value: { thresholdCents: BigInt(dollars) * 100n, backstopDays: days },
      }
    }
  }

  return {
    email,
    firstName,
    lastName,
    companyName,
    plan: form.plan === 'ccc' ? PlanKeyEnum.PLAN_KEY_CCC : PlanKeyEnum.PLAN_KEY_STANDARD,
    trial,
    reason,
  }
}

// Provisions a new customer's account and organization with the chosen plan
// and trial, then emails them a sign-in link (AdminInviteAccount).
export function AccountInviteButton() {
  const [open, setOpen] = useState(false)
  const [preset, setPreset] = useState<PresetKey>('blank')
  const [form, setForm] = useState<AccountInviteForm>(EMPTY_FORM)
  const queryClient = useQueryClient()

  const mutation = useMutation(adminInviteAccount, {
    onSuccess: (response, request) => {
      toast.success(`Invite sent to ${request.email}`, {
        description:
          request.trial?.case === 'volumeTrial'
            ? `${request.companyName} starts with a volume trial (${response.trialDays}-day backstop).`
            : `${request.companyName} starts with a ${response.trialDays}-day trial.`,
      })
      setPreset('blank')
      setForm(EMPTY_FORM)
      setOpen(false)
      void queryClient.invalidateQueries()
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to send invite')
    },
  })

  const request = buildAccountInviteRequest(form)
  const needsReason = inviteNeedsReason(form)
  const update =
    (field: keyof AccountInviteForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }))

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <UserPlus className="h-4 w-4" />
        Invite account
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite an account</DialogTitle>
            <DialogDescription>
              Creates their account and organization with the plan and trial you choose, then
              emails them a sign-in link. The trial starts when they add a payment method.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (request) mutation.mutate(request)
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="account-invite-preset">Start from</Label>
              <Select
                value={preset}
                onValueChange={(value) => {
                  const key = value as PresetKey
                  setPreset(key)
                  setForm((prev) => ({ ...prev, ...PRESETS[key] }))
                }}
              >
                <SelectTrigger id="account-invite-preset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blank">Blank</SelectItem>
                  <SelectItem value="ccc">Coffee Cart Collective member</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="account-invite-first-name">First name</Label>
                <Input
                  id="account-invite-first-name"
                  value={form.firstName}
                  onChange={update('firstName')}
                  autoComplete="off"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="account-invite-last-name">Last name</Label>
                <Input
                  id="account-invite-last-name"
                  value={form.lastName}
                  onChange={update('lastName')}
                  autoComplete="off"
                  required
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="account-invite-email">Email</Label>
              <Input
                id="account-invite-email"
                type="email"
                value={form.email}
                onChange={update('email')}
                autoComplete="off"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="account-invite-company">Company name</Label>
              <Input
                id="account-invite-company"
                value={form.companyName}
                onChange={update('companyName')}
                autoComplete="off"
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="account-invite-plan">Pricing</Label>
                <Select
                  value={form.plan}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, plan: value as InvitePlan }))}
                >
                  <SelectTrigger id="account-invite-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="ccc">Coffee Cart Collective</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="account-invite-trial">Trial</Label>
                <Select
                  value={form.trial}
                  onValueChange={(value) =>
                    setForm((prev) => {
                      const trial = value as InviteTrial
                      if (trial !== 'volume') return { ...prev, trial }
                      return {
                        ...prev,
                        trial,
                        thresholdDollars: prev.thresholdDollars || VOLUME_TRIAL_DEFAULTS.thresholdDollars,
                        trialDays: prev.trialDays || VOLUME_TRIAL_DEFAULTS.trialDays,
                      }
                    })
                  }
                >
                  <SelectTrigger id="account-invite-trial">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="extended">Extended</SelectItem>
                    <SelectItem value="volume">Volume</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.trial === 'volume' && (
              <p className="text-muted-foreground text-sm">
                Free until they process the threshold through Cortado payments, or until the
                backstop, whichever comes first.
              </p>
            )}
            {form.trial !== 'standard' && (
              <div className={form.trial === 'volume' ? 'grid gap-4 sm:grid-cols-2' : undefined}>
                {form.trial === 'volume' && (
                  <div className="grid gap-2">
                    <Label htmlFor="account-invite-threshold">Threshold (USD)</Label>
                    <Input
                      id="account-invite-threshold"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={form.thresholdDollars}
                      onChange={update('thresholdDollars')}
                      required
                    />
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="account-invite-days">
                    {form.trial === 'volume' ? 'Backstop (days)' : 'Trial length (days)'}
                  </Label>
                  <Input
                    id="account-invite-days"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={form.trialDays}
                    onChange={update('trialDays')}
                    required
                  />
                </div>
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="account-invite-reason">
                Reason{needsReason ? '' : ' (optional)'}
              </Label>
              <Textarea
                id="account-invite-reason"
                value={form.reason}
                onChange={update('reason')}
                placeholder="Recorded on the plan eligibility and trial grant"
                required={needsReason}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!request || mutation.isPending}>
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Send invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
